import React from 'react';
import { Box, Rating } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as VerifiedIcon } from '../../assets/icons/bages/varify_small.svg';
import { ReactComponent as EarlyBirdIcon } from '../../assets/icons/bages/early_bird.svg';
import { ReactComponent as StarIcon } from '../../assets/icons/info/star.svg';
import { ReactComponent as EmptyStarIcon } from '../../assets/icons/info/emptyStar.svg';
import {
  ReviewViewModel,
  ReviewStatus,
  KycStatus,
} from '../../configuration/api/apiSchema';
import { formatDistanceToNow } from 'date-fns';
import ClickableAvatar from '../ClickableAvatar/ClickableAvatar';
import { GlassColors } from '../glassmorphism/GlassmorphismStyles';
import { UserRoles } from '../../pages/Auth/actions';

interface ReviewItemProps {
  review: ReviewViewModel;
  onRespond?: (reviewId: string) => void;
  showActions?: boolean;
}

const ReviewItem: React.FC<ReviewItemProps> = ({
  review,
  onRespond,
  showActions = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getStatusColor = (status?: ReviewStatus) => {
    return '#29A322'; // Always green for submitted status in design
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };
  const roleId = review.reviewer?.roleId;

  const isVerified =
    roleId === UserRoles.HandymanRoleId
      ? review.reviewer?.isStripeAccountVerified === true &&
        review.reviewer?.kycStatus === KycStatus.Verified
      : roleId === UserRoles.CustomerRoleId
        ? review.reviewer?.kycStatus === KycStatus.Verified
        : false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        padding: '24px',
        gap: '16px',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
        borderRadius: '32px',
        minHeight: '148px',
        '&:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      {/* Header with Avatar and Status */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          padding: '0px',
          width: '100%',
        }}
      >
        {/* Left: Avatar and User Info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '16px',
            maxWidth: '100%',
          }}
        >
          <ClickableAvatar
            userId={review.reviewer?.id}
            src={review.reviewer?.avatarUrl || undefined}
            alt={`${review.reviewer?.firstName || 'User'}`}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: isDark
                ? 'rgba(0, 92, 173, 0.7)'
                : 'rgba(0, 36, 58, 0.8)',
              color: '#00FFFF',
              boxShadow:
                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '0px',
              maxWidth: 'calc(100% - 56px)',
            }}
          >
            {/* Name and Badge */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
                maxWidth: '100%',
                '& svg path': {
                  fill: isDark ? GlassColors.accent : GlassColors.secondary,
                },
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 800,
                  fontSize: { xs: '16px', sm: '20px' },
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                  maxWidth: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {review.reviewer?.firstName} {review.reviewer?.lastName}
              </Box>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {isVerified && <VerifiedIcon />}
                {review.reviewer?.isEarlyBird && <EarlyBirdIcon />}
              </Box>
            </Box>

            {/* Rating */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
              }}
            >
              <Rating
                value={review.rating || 0}
                readOnly
                precision={0.5}
                size='small'
                icon={<StarIcon />}
                emptyIcon={
                  <EmptyStarIcon style={{ width: '18px', height: '18px' }} />
                }
                sx={{
                  '& .MuiRating-icon': {
                    filter: 'drop-shadow(0px 2px 1px rgba(0,0,0,0.5))',
                  },
                  '& .MuiRating-iconFilled': {
                    color: '#D1AB01',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: '#D1AB01',
                  },
                }}
              />
              <Box
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: { xs: 500, md: 700 },
                  fontSize: { xs: '12px', md: '16px' },
                  lineHeight: '175%',
                  letterSpacing: '0.15px',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              >
                {review.rating}/5
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right: Status and Time */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px',
            gap: '16px',
            ml: 'auto',
          }}
        >
          <Box
            className='bage'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '2px 16px',
              background: '#29A322',
              boxShadow:
                '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
              borderRadius: '999px',
              height: '22px',
            }}
          >
            <Box
              sx={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '18px',
                textAlign: 'right',
                color: isDark ? '#B6B6B6' : '#1A1A1A',
              }}
            >
              {review.status || 'Submitted'}
            </Box>
          </Box>

          <Box
            sx={{
              fontFamily: 'Montserrat',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '166%',
              textAlign: 'right',
              letterSpacing: '0.4px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            {formatDate(review.createdAt)}
          </Box>
        </Box>
      </Box>

      {/* Comment */}
      <Box
        sx={{
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: { xs: '14px', md: '16px' },
          lineHeight: '150%',
          letterSpacing: '0.15px',
          color: isDark ? '#B6B6B6' : '#1A1A1A',
          width: '100%',
        }}
      >
        {review.comment || 'No comment provided.'}
      </Box>
    </Box>
  );
};

export default ReviewItem;
